import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonPage,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useParams } from 'react-router-dom';
import { GET_CAREER_ENTRY_OVERLAPS } from '../../../graphql/queries/publicProfiles';
import type {
  GetCareerEntryOverlapsQuery,
  GetCareerEntryOverlapsQueryVariables,
} from '../../../types/graphql-generated';
import { CAREER_OVERLAPS_PAGE_SIZE } from '../../../appConfig';
import { useInfiniteScrollQuery } from '../../../hooks/useInfiniteScrollQuery';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { PublicProfileCard } from './components/PublicProfileCard';

interface CareerOverlapsPageProps {
  defaultBackHref?: string;
  profilePathPrefix?: string;
}

// People that were in the organization of a career entry during its period.
// Reached by tapping the organization name of a career entry.
export const CareerOverlapsPage = ({
  defaultBackHref = '/app/main/perfil',
  profilePathPrefix = '/app/main/perfil/usuario',
}: CareerOverlapsPageProps) => {
  const careerEntryId = useParams<'careerEntryId'>().careerEntryId!;
  const { data, loading, error, loadMore } = useInfiniteScrollQuery<
    GetCareerEntryOverlapsQuery,
    GetCareerEntryOverlapsQueryVariables
  >({
    query: GET_CAREER_ENTRY_OVERLAPS,
    buildVariables: (offset) => ({
      careerEntryId,
      limit: CAREER_OVERLAPS_PAGE_SIZE,
      offset,
    }),
    pageSize: CAREER_OVERLAPS_PAGE_SIZE,
    append: (previous, next) => {
      const previousOverlaps = previous.careerEntryOverlaps;
      const nextOverlaps = next.careerEntryOverlaps;
      if (!previousOverlaps || !nextOverlaps) return next;

      return {
        careerEntryOverlaps: {
          ...nextOverlaps,
          matches: [...previousOverlaps.matches, ...nextOverlaps.matches],
        },
      };
    },
  });

  const overlaps = data?.careerEntryOverlaps;
  const matches = overlaps?.matches ?? [];
  const hasMore = matches.length < (overlaps?.totalCount ?? 0);

  return (
    <IonPage>
      <AppSectionHeader variant="main">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={defaultBackHref} />
          </IonButtons>
          <IonTitle className="ml-4">Experiencia</IonTitle>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent className="ion-padding">
        {loading && !overlaps && (
          <div className="flex min-h-[200px] items-center justify-center">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && !overlaps && (
          <IonText color="danger">
            <p>No se ha podido cargar la lista de personas</p>
          </IonText>
        )}

        {!loading && !error && !overlaps && (
          <div className="flex min-h-[200px] items-center justify-center">
            <IonText color="medium">
              <p>No se ha encontrado la experiencia.</p>
            </IonText>
          </div>
        )}

        {overlaps && (
          <>
            <h2
              className="mb-4 font-heading text-gray-900"
              style={{ marginTop: 0, fontSize: '18px', fontWeight: 800, lineHeight: 1.25 }}
            >
              Más gente en {overlaps.careerOrganization.name} en este periodo
            </h2>
            {matches.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[#cfd5df] bg-white p-5 text-center">
                <p className="m-0 text-sm text-slate-500">
                  Todavía no hay más gente que coincidiera en este periodo
                </p>
              </div>
            ) : (
              <>
                {matches.map(({ profile, careerEntries }) => (
                  <PublicProfileCard
                    key={profile.id}
                    profile={profile}
                    matchingCareerEntries={careerEntries}
                    navigable
                    profilePathPrefix={profilePathPrefix}
                  />
                ))}
                {hasMore && (
                  <IonInfiniteScroll onIonInfinite={loadMore} threshold="100px">
                    <IonInfiniteScrollContent
                      loadingSpinner="bubbles"
                      loadingText="Cargando más personas..."
                    />
                  </IonInfiniteScroll>
                )}
              </>
            )}
          </>
        )}
      </IonContent>
    </IonPage>
  );
};
